"use client"

import { useEffect, useMemo, useState } from "react"
import { getCoupons, createCoupon as createCouponAPI, bulkCreateCoupons } from "../utils/api"
import * as XLSX from "xlsx"

export default function CouponManagement() {
  const [coupons, setCoupons] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [couponCode, setCouponCode] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [filterBy, setFilterBy] = useState("All")
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [toastType, setToastType] = useState("success")
  const [loading, setLoading] = useState(false)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const showToastMessage = (message, type = "success") => {
    setToastMessage(message)
    setToastType(type)
    setShowToast(true)
    setTimeout(() => setShowToast(false), 3000)
  }

  const normalize = (c) => {
    // normalize backend object to the expected UI shape
    const id = c._id || c.id || (c._doc && c._doc._id) || null
    const createdAt = c.createdAt || c.created_date || (c.createdAt && c.createdAt.$date) || c.createdAt
    const createdDate = createdAt ? new Date(createdAt).toLocaleDateString() : new Date().toLocaleDateString()
    const createdTime = createdAt ? new Date(createdAt).toLocaleTimeString() : new Date().toLocaleTimeString()

    return {
      id,
      code: (c.code || "").toUpperCase(),
      status: (c.status || "active").toLowerCase(),
      startupId: c.startupId || c.startupID || c.startup_id || c.startup || "",
      startupName: c.startupName || c.startup_name || c.startup || "",
      createdDate,
      createdTime,
      raw: c,
    }
  }

  const fetchCoupons = async () => {
    try {
      setLoading(true)
      const data = await getCoupons() // supports query params if needed
      // data might be { data: [...]} or an array depending on backend; handle both
      const arr = Array.isArray(data) ? data : data.data || data.coupons || []
      setCoupons(arr.map(normalize))
    } catch (err) {
      console.error(err)
      showToastMessage(err?.message || "Failed to fetch coupons", "error")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCoupons()
  }, [])

  // Reset to first page whenever filters/search or itemsPerPage changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, filterBy, itemsPerPage, coupons.length])

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      showToastMessage("Please upload an Excel file (.xlsx or .xls)", "error")
      return
    }

    try {
      setLoading(true) // show loader
      const reader = new FileReader()

      reader.onload = async (event) => {
        try {
          const data = new Uint8Array(event.target.result)
          const workbook = XLSX.read(data, { type: "array" })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" })

          const couponsToCreate = rows.map((row) => ({
            code: (row.Codes || row.Code || row.code || "").toString().toUpperCase(),
            startupName: row.StartupName || row.startupName || undefined,
            startupId: row.StartupId || row.StartupID || row.startupId || undefined,
            status: (row.Status || "active").toString().toLowerCase(),
          }))

          const res = await bulkCreateCoupons(couponsToCreate)

          // Try to support different response shapes
          const createdArr = res?.coupons || res?.data || res?.created || res?.inserted || []
          const normalizedCreated = (Array.isArray(createdArr) ? createdArr : []).map(normalize)

          setCoupons((prev) => [...prev, ...normalizedCreated])

          const insertedCount = res?.insertedCount ?? normalizedCreated.length
          showToastMessage(`Imported ${insertedCount} coupons from Excel`)
          e.target.value = ""
          setIsModalOpen(false)
        } catch (error) {
          console.error("Bulk import error:", error)
          showToastMessage(error?.message || "Failed to import coupons", "error")
        } finally {
          setLoading(false) // hide loader after processing
        }
      }

      reader.readAsArrayBuffer(file)
    } catch (err) {
      console.error("Bulk import error:", err)
      showToastMessage(err?.message || "Failed to import coupons", "error")
      setLoading(false) // make sure loader hides on error
    }
  }

  const handleManualSubmit = async (e) => {
    e.preventDefault()
    if (!couponCode.trim()) {
      showToastMessage("Please enter a coupon code", "error")
      return
    }

    const payload = {
      code: couponCode.toUpperCase(),
      status: "active",
    }

    try {
      const created = await createCouponAPI(payload)
      // created may be coupon doc or wrapper; try to extract
      const createdDoc = created && created._id ? created : created.data || created
      setCoupons((prev) => [...prev, normalize(createdDoc)])
      setCouponCode("")
      setIsModalOpen(false)
      showToastMessage("Coupon added successfully")
    } catch (err) {
      console.error(err)
      showToastMessage(err?.message || "Failed to create coupon", "error")
    }
  }

  const handleDelete = (id) => {
    // No delete endpoint shown in your backend snippets; keep client-only deletion for now
    setCoupons((prev) => prev.filter((coupon) => coupon.id !== id))
    showToastMessage("Coupon deleted successfully")
  }

  // Filter logic (safe access)
  const filteredCoupons = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    return coupons.filter((coupon) => {
      const code = (coupon.code || "").toLowerCase()
      const startupName = (coupon.startupName || "").toLowerCase()
      const matchesSearch =
        q === "" || code.includes(q) || startupName.includes(q)
      const matchesFilter = filterBy === "All" || (coupon.status || "").toLowerCase() === filterBy.toLowerCase()
      return matchesSearch && matchesFilter
    })
  }, [coupons, searchTerm, filterBy])

  // Pagination calculations
  const totalItems = filteredCoupons.length
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage))
  const currentPageSafe = Math.min(currentPage, totalPages)
  const startIndex = (currentPageSafe - 1) * itemsPerPage
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems)
  const paginatedCoupons = filteredCoupons.slice(startIndex, endIndex)

  // Helper to generate page number list with simple ellipses for long lists
  const getPageNumbers = (total, current) => {
    const pages = []
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i)
      return pages
    }
    // total > 7
    pages.push(1)
    const left = Math.max(2, current - 1)
    const right = Math.min(total - 1, current + 1)

    if (left > 2) pages.push("left-ellipsis")
    for (let i = left; i <= right; i++) pages.push(i)
    if (right < total - 1) pages.push("right-ellipsis")
    pages.push(total)
    return pages
  }

  const pageNumbers = getPageNumbers(totalPages, currentPageSafe)

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {showToast && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${toastType === "error" ? "bg-red-500 text-white" : "bg-green-500 text-white"
            }`}
        >
          {toastMessage}
        </div>
      )}

      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-blue-500 p-3 rounded-xl">
                <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-blue-900">Coupon Management</h1>
                <p className="text-blue-700 text-base">Manage and track coupon codes</p>
              </div>
            </div>
            <div>
              {loading ? <span className="text-sm text-gray-600">Loading...</span> : null}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center gap-4 mb-4">
            <svg className="h-5 w-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900">Search & Filter</h3>
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Filter By</label>
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Expired">Expired</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Search Term</label>
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  placeholder="Search coupons..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Coupon
              </button>
            </div>
          </div>
        </div>

        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Add New Coupon</h2>
                <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-gray-600 mb-6">Choose how you want to add coupons to your system</p>

              <div className="space-y-6">
                {/* Manual Entry */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <svg className="h-4 w-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Manual Entry
                  </h4>
                  <form onSubmit={handleManualSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Coupon Code</label>
                      <input
                        placeholder="Enter coupon code (e.g., STARTUP50)"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Coupon
                    </button>
                  </form>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">Or</span>
                  </div>
                </div>

                {/* Excel Import */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <svg className="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Import from Excel
                  </h4>
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                    <svg
                      className="h-8 w-8 mx-auto text-gray-400 mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <p className="text-sm text-gray-600 mb-3">Upload Excel file with coupon codes</p>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Rows per page</label>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="text-sm text-gray-600">
              Showing <span className="font-medium">{totalItems === 0 ? 0 : startIndex + 1}</span> to{" "}
              <span className="font-medium">{endIndex}</span> of <span className="font-medium">{totalItems}</span> coupons
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left font-semibold text-gray-700 py-4 px-4">S.No</th>
                  <th className="text-left font-semibold text-gray-700 px-4">COUPON CODE</th>
                  <th className="text-left font-semibold text-gray-700 px-4">STARTUP ID</th>
                  <th className="text-left font-semibold text-gray-700 px-4">STARTUP NAME</th>
                  <th className="text-left font-semibold text-gray-700 px-4">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCoupons.map((coupon, localIndex) => (
                  <tr key={coupon.id || `${startIndex + localIndex}`} className={localIndex % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                    <td className="py-4 px-4 text-gray-900">{startIndex + localIndex + 1}</td>
                    <td className="px-4 font-mono font-semibold text-blue-600">{coupon.code}</td>
                    <td className="px-4 font-mono text-gray-600">{coupon.startupId || "Not Assigned"}</td>
                    <td className="px-4 text-gray-900">{coupon.startupName || "Not Assigned"}</td>
                    <td className="px-4">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${coupon.status === "active"
                          ? "bg-green-100 text-green-700 border-green-200"
                          : "bg-red-100 text-red-700 border-red-200"
                          }`}
                      >
                        {coupon.status === "active" ? "Active" : "Expired"}
                      </span>
                    </td>
                  </tr>
                ))}
                {paginatedCoupons.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-gray-500">
                      No coupons found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPageSafe === 1}
                className={`px-3 py-1 rounded-md border ${currentPageSafe === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100"}`}
                aria-label="First page"
              >
                « First
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPageSafe === 1}
                className={`px-3 py-1 rounded-md border ${currentPageSafe === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100"}`}
                aria-label="Previous page"
              >
                ‹ Prev
              </button>
            </div>

            <div className="flex items-center gap-2">
              {pageNumbers.map((p, idx) =>
                typeof p === "number" ? (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`px-3 py-1 rounded-md border ${p === currentPageSafe ? "bg-blue-500 text-white" : "hover:bg-gray-100"}`}
                    aria-current={p === currentPageSafe ? "page" : undefined}
                  >
                    {p}
                  </button>
                ) : (
                  <span key={`${p}-${idx}`} className="px-2 text-gray-400">
                    ...
                  </span>
                )
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPageSafe === totalPages}
                className={`px-3 py-1 rounded-md border ${currentPageSafe === totalPages ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100"}`}
                aria-label="Next page"
              >
                Next ›
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPageSafe === totalPages}
                className={`px-3 py-1 rounded-md border ${currentPageSafe === totalPages ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100"}`}
                aria-label="Last page"
              >
                Last »
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
